import React, { FunctionComponent, useState } from "react"
import * as yup from "yup"
import { Grid, TextField, LinearProgress, Typography, Box, FormHelperText } from "@material-ui/core"
import { BatchItem } from "./BatchItem"
import styles from "./BatchItemCard.module.scss"
import { useBatchPageContext } from "./useBatchPageContext"
import { AudioPlayer } from "../../components/AudioPlayer"
import { useStepControlsContext } from "../../features/StepControls/useStepControlsContext"
import { LoadingButton } from "../../components/LoadingButton"
import { FormikProps } from "formik"
import { FormValues } from "./validation/interfaces"
import { formHasValidationErrors } from "./validation/ValidationHelpers"
import {
    sourceTextValidationSchema,
    targetTextValidationSchema
} from "./validation/ValidationSchemas"
interface BatchItemCardProps {
    item: BatchItem
}

export const BatchItemCard: FunctionComponent<BatchItemCardProps &
    FormikProps<FormValues>> = props => {
    const { item } = props
    // formik props
    const { values, errors, handleChange, handleBlur, validateForm } = props
    const {
        generateSingleSourceAudio,
        generateSingleTargetAudio,
        translateText,
        state
    } = useBatchPageContext()
    const { state: stepper } = useStepControlsContext()
    const [sourceAudioLoading, setSourceAudioLoading] = useState(false)
    const [targetAudioLoading, setTargetAudioLoading] = useState(false)
    const [rowTranslateLoading, setRowTranslateLoading] = useState(false)

    const regenerateSourceAudio = async () => {
        const valid = await sourceTextValidationSchema.isValid(values.source)
        if (!valid) return
        const params = { ...item, source: values.source }
        setSourceAudioLoading(true)
        generateSingleSourceAudio(params).then(() => {
            setSourceAudioLoading(false)
        })
    }

    const regenerateTargetAudio = async () => {
        const valid = await targetTextValidationSchema.isValid(values.target)
        if (!valid) return
        const params = { ...item, target: values.target }
        setTargetAudioLoading(true)
        generateSingleTargetAudio(params).then(() => {
            setTargetAudioLoading(false)
        })
    }

    const translateRow = async (item: BatchItem) => {
        const valid = await sourceTextValidationSchema.isValid(values.source)
        if (!valid) return
        const params = { ...item, source: values.source }
        setRowTranslateLoading(true)
        translateText(params).then(() => {
            setRowTranslateLoading(false)
        })
    }

    const sourceEditable = () => {
        return !item.downloadableSourceAudio
    }

    const targetEditable = () => {
        return !item.downloadableTargetAudio
    }

    const translateTextDisabled = !!(formHasValidationErrors(errors) || targetAudioLoading)

    const generateSourceAudioDisabled = !!(
        errors.name ||
        errors.fileNameError ||
        errors.source ||
        sourceAudioLoading
    )

    const generateTargetAudioDisabled = !!(
        errors.name ||
        errors.fileNameError ||
        errors.target ||
        targetAudioLoading
    )

    const getCurrentFileFormatExtension = () => {
        return state.audioFormatOptions[parseInt(state.fields.fileFormat.value)].type
    }

    return (
        <Box>
            <Grid container direction="column">
                <Grid container alignItems="flex-start" justify="space-between" direction="row">
                    <Grid item className={styles.BatchItemCard__textInput}>
                        <Grid container direction="column" spacing={1}>
                            <Grid item data-testid={"sourceTextGridItem"}>
                                <TextField
                                    name="source"
                                    required
                                    value={values.source || ""} //https://stackoverflow.com/a/54226170/15061927
                                    inputProps={{ "data-testid": "sourceField" }}
                                    multiline
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth={true}
                                    rows={10}
                                    label="Source Text"
                                    variant="outlined"
                                    disabled={!sourceEditable()}
                                    error={!!errors.source && item.include}
                                />
                                <FormHelperText error>
                                    {item.include ? errors.source : undefined}
                                    {item.include ? errors.translationError : undefined}
                                </FormHelperText>
                            </Grid>
                            {sourceAudioLoading && (
                                <Grid item>
                                    <LinearProgress color={"secondary"} />
                                </Grid>
                            )}
                            {!sourceAudioLoading && (
                                <Grid item data-testid={"sourceAudioGridItem"}>
                                    <AudioPlayer
                                        className={styles.BatchItemCard__audioPlayer}
                                        playerId={item.name}
                                        audioFile={item.sourceAudio}
                                        loading={sourceAudioLoading}
                                        errorMsg={errors.sourceAudioError}
                                    />
                                </Grid>
                            )}
                            {stepper.activeStep === 1 && (
                                <Grid item>
                                    <LoadingButton
                                        buttonId={"translate-row-btn"}
                                        disabled={translateTextDisabled || rowTranslateLoading}
                                        loading={rowTranslateLoading}
                                        text={"Translate"}
                                        onClick={() => translateRow(item)}
                                    />
                                </Grid>
                            )}
                            {stepper.activeStep === 2 && (
                                <Grid item>
                                    <LoadingButton
                                        buttonId={"source-audio-regenerate-button"}
                                        disabled={generateSourceAudioDisabled}
                                        loading={sourceAudioLoading}
                                        text={"Generate Audio"}
                                        onClick={regenerateSourceAudio}
                                    />
                                </Grid>
                            )}
                            {item.sourceAudio && stepper.activeStep === 3 && (
                                <Grid item>
                                    <Typography data-testid="source-filename">
                                        {item.sourceAudio.fileName}.
                                        {getCurrentFileFormatExtension()}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    <Grid item className={styles.BatchItemCard__textInput}>
                        <Grid container direction="column" spacing={1}>
                            <Grid item data-testid={"targetTextGridItem"}>
                                <TextField
                                    name="target"
                                    value={values.target || ""} //https://stackoverflow.com/a/54226170/15061927
                                    inputProps={{ "data-testid": "targetField" }}
                                    multiline
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth={true}
                                    rows={10}
                                    label="Target Text"
                                    variant="outlined"
                                    disabled={!targetEditable()}
                                    error={!!errors.target && item.include}
                                    helperText={item.include ? errors.target : undefined}
                                />
                            </Grid>
                            {targetAudioLoading && (
                                <Grid item>
                                    <LinearProgress color={"secondary"} />
                                </Grid>
                            )}
                            {!targetAudioLoading && (
                                <Grid item data-testid={"targetAudioGridItem"}>
                                    <AudioPlayer
                                        className={styles.BatchItemCard__audioPlayer}
                                        playerId={item.name}
                                        audioFile={item.targetAudio}
                                        loading={targetAudioLoading}
                                        errorMsg={errors.targetAudioError}
                                    />
                                </Grid>
                            )}
                            {item.target && stepper.activeStep === 2 && (
                                <Grid item>
                                    <LoadingButton
                                        buttonId={"target-audio-regenerate-button"}
                                        disabled={generateTargetAudioDisabled}
                                        loading={targetAudioLoading}
                                        text={"Generate Audio"}
                                        onClick={regenerateTargetAudio}
                                    />
                                </Grid>
                            )}
                            {item.targetAudio && stepper.activeStep === 3 && (
                                <Grid item>
                                    <Typography data-testid="target-filename">
                                        {item.targetAudio.fileName}.
                                        {getCurrentFileFormatExtension()}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    )
}
